import { RefObject, useCallback, useEffect, useRef } from 'react';
import {
  CompositeLayoutData,
  GraphComponent,
  HierarchicalLayout,
  IFoldingView,
  IGraph,
  ILayoutAlgorithm,
  INode,
  ItemEventArgs,
  LayoutData,
  Rect,
} from '@ardoq/yfiles';
import { GroupFoldState, Layout, Selection } from './props';
import {
  constructLayoutAlgorithm,
  constructLayoutData,
} from './yfilesConstructors';
import { NodeTag, nodeId } from './tags';

/**
 * Builds the layout algorithm + data for `layoutProps` *without* applying it, for an
 * animated relayout via `applyLayoutAnimated` (which computes and morphs to the result
 * itself). With `fromSketch`, a *hierarchical* layout runs in `fromSketchMode` so it
 * reflows from the current drawing and only nudges the affected region — used on
 * fold/unfold. `fromSketch` is a no-op for other layouts (tree etc. have no sketch mode),
 * which recompute from scratch; that's deterministic and still reclaims the freed space,
 * just with a bit more movement. Without `fromSketch` (the "optimize layout" action), even
 * hierarchical recomputes cleanly from scratch.
 */
const buildLayout = (
  graph: IGraph,
  layoutProps: Layout,
  { fromSketch = false }: { fromSketch?: boolean } = {}
) => {
  const layoutAlgorithm = constructLayoutAlgorithm(
    layoutProps.algorithm,
    graph
  );
  if (fromSketch && layoutAlgorithm instanceof HierarchicalLayout) {
    layoutAlgorithm.fromSketchMode = true;
  }
  const layoutData = new CompositeLayoutData(
    constructLayoutData(layoutProps.algorithm, graph)
  );
  return { layoutAlgorithm, layoutData };
};

type LayoutRef = RefObject<{
  algorithm: ILayoutAlgorithm;
  data: LayoutData;
} | null>;

type ReconcileSelection = (
  graphComponent: GraphComponent,
  selection: Selection
) => void;

/**
 * Shrink a just-collapsed group's folder node to its `collapsedSize` (defaulting to the
 * group's min `size` per the prop contract) so it doesn't linger at full size as an empty
 * box. No-op for non-`canvas-group` nodes.
 */
const applyCollapsedSize = (
  graphComponent: GraphComponent,
  node: INode
): void => {
  const style = (node.tag as NodeTag | undefined)?.node?.style;
  if (style?.type !== 'canvas-group') return;
  const collapsedSize = style.collapsedSize ?? style.size;
  const { x, y } = node.layout;
  graphComponent.graph.setNodeLayout(
    node,
    new Rect(x, y, collapsedSize.width, collapsedSize.height)
  );
};

/**
 * View nodes that stand for a group — both expanded group nodes and collapsed folders.
 * A collapsed group becomes a *folder* node in the view, for which `graph.isGroupNode` is
 * false, so we can't detect groups by the view alone; instead we map each view node to its
 * master node (always a group node in the master graph) which covers both states.
 */
const groupViewNodes = (
  graphComponent: GraphComponent,
  foldingView: IFoldingView
): INode[] => {
  const { masterGraph } = foldingView.manager;
  return Array.from(graphComponent.graph.nodes).filter(node => {
    const masterNode = foldingView.getMasterItem(node);
    return masterNode !== null && masterGraph.isGroupNode(masterNode);
  });
};

/** How many groups are in the view graph, and how many of those are collapsed. */
const computeFoldState = (
  graphComponent: GraphComponent,
  foldingView: IFoldingView
): GroupFoldState => {
  const nodes = groupViewNodes(graphComponent, foldingView);
  return {
    groupCount: nodes.length,
    collapsedCount: nodes.filter(node => !foldingView.isExpanded(node)).length,
  };
};

/** Re-run the view layout in `fromSketchMode` and re-apply selection (shared by folds). */
const runFoldRelayout = (
  graphComponent: GraphComponent,
  layout: Layout,
  layoutRef: LayoutRef,
  selection: Selection | undefined,
  reconcileSelection: ReconcileSelection
): void => {
  const { layoutAlgorithm, layoutData } = buildLayout(
    graphComponent.graph,
    layout,
    { fromSketch: true }
  );
  layoutRef.current = { algorithm: layoutAlgorithm, data: layoutData };
  // `animateViewport: true` fits the (re)compacted graph into view as part of the same
  // morph — collapsing/expanding a group otherwise leaves the diagram off-center or
  // partly out of frame. Honours the component's `limitFitContentZoom` set at init.
  graphComponent.applyLayoutAnimated({
    layout: layoutAlgorithm,
    layoutData,
    animationDuration: layout.morphDuration,
    animateViewport: true,
    targetBoundsPadding: layout.fitGraphBoundsInsets,
  });
  if (selection) reconcileSelection(graphComponent, selection);
};

type UseFoldingArgs = {
  graphComponentRef: RefObject<GraphComponent | null>;
  foldingViewRef: RefObject<IFoldingView | null>;
  /** Shared with the host hook's morph effect, so a fold relayout updates it too. */
  layoutRef: LayoutRef;
  layout: Layout;
  selection: Selection | undefined;
  collapsedGroupIds: ReadonlySet<string> | undefined;
  relayoutToken: unknown;
  collapseAllToken: unknown;
  expandAllToken: unknown;
  onFoldStateChange: ((state: GroupFoldState) => void) | undefined;
  reconcileSelection: ReconcileSelection;
};

/**
 * Encapsulates the folding-specific effects of {@link useYFiles}: re-running layout on
 * collapse/expand, applying the initial collapsed set, and the "optimize layout" trigger.
 * No-op unless the host opted into folding (`foldingViewRef` is set at mount).
 */
export const useFolding = ({
  graphComponentRef,
  foldingViewRef,
  layoutRef,
  layout,
  selection,
  collapsedGroupIds,
  relayoutToken,
  collapseAllToken,
  expandAllToken,
  onFoldStateChange,
  reconcileSelection,
}: UseFoldingArgs): void => {
  const collapsedGroupIdsRef = useRef(collapsedGroupIds);
  const lastRelayoutToken = useRef(relayoutToken);
  const lastCollapseAllToken = useRef(collapseAllToken);
  const lastExpandAllToken = useRef(expandAllToken);
  // Lets the initial-collapse and bulk fold-all effects fold many groups but relayout and
  // report fold state only once (the fold listeners would otherwise fire per group), and
  // run the initial collapse exactly once.
  const suppressRelayoutRef = useRef(false);
  const didInitialCollapseRef = useRef(false);
  // Kept in a ref so an unstable callback doesn't re-subscribe the fold listeners.
  const onFoldStateChangeRef = useRef(onFoldStateChange);
  useEffect(() => {
    onFoldStateChangeRef.current = onFoldStateChange;
  }, [onFoldStateChange]);

  const reportFoldState = useCallback(
    (graphComponent: GraphComponent, foldingView: IFoldingView): void =>
      onFoldStateChangeRef.current?.(
        computeFoldState(graphComponent, foldingView)
      ),
    []
  );

  // yFiles' folding view is a graph-model abstraction — it hides/aggregates children but
  // never re-runs layout (there's no relayout-on-fold option). So on collapse/expand we
  // re-run the view's layout ourselves (the documented pattern, per yFiles' folding-with-
  // layout sample) to reclaim the space a collapsed group frees. The full layout also
  // routes the aggregated folding edges, so no separate edge-routing pass is needed.
  // `fromSketchMode` reflows from the current drawing (stable, not a reshuffle) and the
  // relayout fits the recompacted graph back into view (see `runFoldRelayout`).
  useEffect(() => {
    const graphComponent = graphComponentRef.current;
    const foldingView = foldingViewRef.current;
    if (!graphComponent || !foldingView) return;

    // Batched folds (initial collapse, collapse-all/expand-all) suppress the per-group
    // relayout + fold-state report and run a single one themselves.
    const onCollapsed = (evt: ItemEventArgs<INode>) => {
      applyCollapsedSize(graphComponent, evt.item);
      if (!suppressRelayoutRef.current) {
        runFoldRelayout(
          graphComponent,
          layout,
          layoutRef,
          selection,
          reconcileSelection
        );
        reportFoldState(graphComponent, foldingView);
      }
    };
    const onExpanded = () => {
      if (!suppressRelayoutRef.current) {
        runFoldRelayout(
          graphComponent,
          layout,
          layoutRef,
          selection,
          reconcileSelection
        );
        reportFoldState(graphComponent, foldingView);
      }
    };

    foldingView.addEventListener('group-collapsed', onCollapsed);
    foldingView.addEventListener('group-expanded', onExpanded);
    return () => {
      foldingView.removeEventListener('group-collapsed', onCollapsed);
      foldingView.removeEventListener('group-expanded', onExpanded);
    };
  }, [
    graphComponentRef,
    foldingViewRef,
    layoutRef,
    selection,
    layout,
    reconcileSelection,
    reportFoldState,
  ]);

  // Initial collapse: fold the requested groups once. The `group-collapsed` listener above
  // runs per group (setting each folder's collapsedSize), but we suppress its per-group
  // relayout and run a single one at the end. `graph.contains` guards against a node that
  // was detached mid-loop — collapsing an outer group removes its (also-requested) nested
  // group from the view graph. Guarded to run only once, when the graph is first ready.
  useEffect(() => {
    if (didInitialCollapseRef.current) return;
    const graphComponent = graphComponentRef.current;
    const foldingView = foldingViewRef.current;
    if (!graphComponent || !foldingView) return;
    didInitialCollapseRef.current = true;
    const ids = collapsedGroupIdsRef.current;

    if (ids?.size) {
      suppressRelayoutRef.current = true;
      let collapsedAny = false;
      // Snapshot the node list first — collapsing mutates the view graph mid-iteration.
      for (const node of Array.from(graphComponent.graph.nodes)) {
        const id = nodeId(node);
        if (
          id !== undefined &&
          ids.has(id) &&
          graphComponent.graph.contains(node) &&
          graphComponent.graph.isGroupNode(node) &&
          foldingView.isExpanded(node)
        ) {
          foldingView.collapse(node);
          collapsedAny = true;
        }
      }
      suppressRelayoutRef.current = false;
      if (collapsedAny) {
        runFoldRelayout(
          graphComponent,
          layout,
          layoutRef,
          selection,
          reconcileSelection
        );
      }
    }
    // Report the starting fold state so collapse-all/expand-all affordances are correct
    // from the first render, whether or not any group started collapsed.
    reportFoldState(graphComponent, foldingView);
  }, [
    graphComponentRef,
    foldingViewRef,
    collapsedGroupIdsRef,
    layoutRef,
    layout,
    selection,
    reconcileSelection,
    reportFoldState,
  ]);

  // "Optimize layout": when `relayoutToken` changes, recompute the current layout cleanly
  // from scratch and animate to it — the escape hatch for reclaiming space after folds.
  // `layout`/`selection` are in the deps so their current values are read, but the last-
  // token guard means only a token change triggers a relayout (not a layout/selection
  // change), which also skips the initial render.
  useEffect(() => {
    if (relayoutToken === lastRelayoutToken.current) return;
    lastRelayoutToken.current = relayoutToken;
    const graphComponent = graphComponentRef.current;
    if (!graphComponent) return;
    const { layoutAlgorithm, layoutData } = buildLayout(
      graphComponent.graph,
      layout
    );
    layoutRef.current = { algorithm: layoutAlgorithm, data: layoutData };
    graphComponent.applyLayoutAnimated({
      layout: layoutAlgorithm,
      layoutData,
      animationDuration: layout.morphDuration,
      targetBoundsPadding: layout.fitGraphBoundsInsets,
    });
    if (selection) reconcileSelection(graphComponent, selection);
  }, [
    graphComponentRef,
    layoutRef,
    relayoutToken,
    layout,
    selection,
    reconcileSelection,
  ]);

  // Collapse all: fold every group currently in the view in one batch (collapsing a parent
  // hides its descendants, so a single pass over the visible groups fully collapses the
  // diagram), suppressing the per-group relayout/report for a single one at the end.
  useEffect(() => {
    if (collapseAllToken === lastCollapseAllToken.current) return;
    lastCollapseAllToken.current = collapseAllToken;
    const graphComponent = graphComponentRef.current;
    const foldingView = foldingViewRef.current;
    if (!graphComponent || !foldingView) return;

    suppressRelayoutRef.current = true;
    let changedAny = false;
    for (const node of groupViewNodes(graphComponent, foldingView)) {
      if (graphComponent.graph.contains(node) && foldingView.isExpanded(node)) {
        foldingView.collapse(node);
        changedAny = true;
      }
    }
    suppressRelayoutRef.current = false;
    if (changedAny) {
      runFoldRelayout(
        graphComponent,
        layout,
        layoutRef,
        selection,
        reconcileSelection
      );
    }
    reportFoldState(graphComponent, foldingView);
  }, [
    graphComponentRef,
    foldingViewRef,
    collapseAllToken,
    layoutRef,
    layout,
    selection,
    reconcileSelection,
    reportFoldState,
  ]);

  // Expand all: expanding a group reveals nested collapsed groups, so re-scan and expand
  // until none remain (bounded, unlike Block Diagram's unbounded recursion), then a single
  // relayout. The batch suppresses the listeners' per-group relayout/report.
  useEffect(() => {
    if (expandAllToken === lastExpandAllToken.current) return;
    lastExpandAllToken.current = expandAllToken;
    const graphComponent = graphComponentRef.current;
    const foldingView = foldingViewRef.current;
    if (!graphComponent || !foldingView) return;

    suppressRelayoutRef.current = true;
    let changedAny = false;
    const maxPasses = foldingView.manager.masterGraph.nodes.size + 1;
    for (let pass = 0; pass < maxPasses; pass++) {
      const collapsedGroups = groupViewNodes(
        graphComponent,
        foldingView
      ).filter(node => !foldingView.isExpanded(node));
      if (collapsedGroups.length === 0) break;
      for (const node of collapsedGroups) {
        foldingView.expand(node);
        changedAny = true;
      }
    }
    suppressRelayoutRef.current = false;
    if (changedAny) {
      runFoldRelayout(
        graphComponent,
        layout,
        layoutRef,
        selection,
        reconcileSelection
      );
    }
    reportFoldState(graphComponent, foldingView);
  }, [
    graphComponentRef,
    foldingViewRef,
    expandAllToken,
    layoutRef,
    layout,
    selection,
    reconcileSelection,
    reportFoldState,
  ]);
};
